import { DeferredBarChart } from "@prismicio/editor-fields"
import {
  addDays,
  addMonths,
  addWeeks,
  type DateRange,
  differenceInMonths,
  getSimpleISOString,
  getWeekNumber,
  startOfMonth,
  startOfWeek,
} from "@prismicio/editor-support/Date"
import { capitalize } from "@prismicio/editor-support/String"
import {
  BarChartLegend,
  BlankSlate,
  BlankSlateIcon,
  BlankSlateTitle,
  Card,
  CardContent,
  CardFooter,
  Skeleton,
} from "@prismicio/editor-ui"
import { type ReactNode, useMemo } from "react"

import type { Granularity } from "../../app/services/usage"
import {
  type UsageByDate,
  usageSummaryItemTypes,
} from "./usageSummaryItemTypes"
import { formatValue } from "./usageUtils"
import { useUsageQuery } from "./useUsage"

interface UsageChartCardProps {
  itemTypeKey: keyof typeof usageSummaryItemTypes
  selectedDateRange: DateRange
}

const keys = ["content", "media"]
const keyLabels = keys.map((key) => capitalize(key))

export function UsageChartCard(props: UsageChartCardProps) {
  const {
    itemTypeKey,
    selectedDateRange: { startDate, endDate },
  } = props

  const granularity = getGranularity(startDate, endDate)

  const { data: usage } = useUsageQuery(
    startDate,
    addDays(endDate, 1),
    granularity,
  )

  const itemType = usageSummaryItemTypes[itemTypeKey]
  const { chartUnit: unit, chartYAxisLabel } = itemType
  const usageByDate = itemType.getUsageByDate(usage)

  const hasUsageInSelectedRange = usageByDate.some(
    (usage) => usage.content !== 0 || usage.media !== 0,
  )

  const filledUsageByDate = useMemo(() => {
    return hasUsageInSelectedRange
      ? fillMissingDatesWithZeros({
          usageByDate,
          startDate,
          endDate,
          granularity,
        })
      : usageByDate
  }, [hasUsageInSelectedRange, usageByDate, startDate, endDate, granularity])

  if (!hasUsageInSelectedRange)
    return <UsageChartCardTemplate chart={<UsageChartCardBlankSlate />} />

  function formatYAxisValue(value: number): string {
    return formatValue(value, { unit, notation: "compact" })
  }

  function formatTooltipValue(value: number | string | Date): string {
    if (!(typeof value === "number")) return value.toString()

    return formatValue(value, { unit, notation: "standard" })
  }

  const { formatXAxisValue, xAxisLegend } =
    granularityToChartConfig[granularity]

  return (
    <UsageChartCardTemplate
      chart={
        <DeferredBarChart
          data={filledUsageByDate}
          keys={keys}
          indexBy="date"
          height={330}
          xAxisLegend={xAxisLegend}
          yAxisLegend={chartYAxisLabel}
          formatYAxisValue={formatYAxisValue}
          formatXAxisValue={formatXAxisValue}
          formatTooltipValue={formatTooltipValue}
          formatTooltipLabel={formatTooltipLabel}
        />
      }
      legend={<BarChartLegend keyLabels={keyLabels} />}
    />
  )
}

interface UsageChartCardTemplateProps {
  chart: ReactNode
  legend?: ReactNode
}

function UsageChartCardTemplate(props: UsageChartCardTemplateProps) {
  const { chart, legend } = props
  return (
    <Card paddingBlock={32} paddingInline={24} variant="outlined">
      <CardContent>
        {chart}
        {legend && <CardFooter>{legend}</CardFooter>}
      </CardContent>
    </Card>
  )
}

function UsageChartCardBlankSlate() {
  return (
    <BlankSlate inlinePadding={false}>
      <BlankSlateIcon
        name="alert"
        backgroundColor="purple3"
        lineColor="purple11"
        size="small"
      />
      <BlankSlateTitle color="grey11" size="medium">
        No historical data
      </BlankSlateTitle>
    </BlankSlate>
  )
}

export function UsageChartCardSkeleton() {
  return (
    <UsageChartCardTemplate
      chart={<Skeleton height={330} width="100%" />}
      legend={<BarChartLegend keyLabels={keyLabels} />}
    />
  )
}

function getGranularity(startDate: Date, endDate: Date): Granularity {
  const diffInMonths = differenceInMonths(startDate, endDate)
  if (diffInMonths >= 6) return "monthly"
  if (diffInMonths >= 1) return "weekly"
  return "daily"
}

interface FillMissingDatesWithZerosArgs {
  usageByDate: UsageByDate[]
  startDate: Date
  endDate: Date
  granularity: Granularity
}

function fillMissingDatesWithZeros(
  args: FillMissingDatesWithZerosArgs,
): UsageByDate[] {
  const { usageByDate, startDate, endDate, granularity } = args

  const result: UsageByDate[] = []
  const usageMap = new Map<string, UsageByDate>()

  usageByDate.forEach((dateData) => {
    usageMap.set(dateData.date, dateData)
  })

  const { alignStartDate, incrementDate } =
    granularityToChartConfig[granularity]

  let currentDate = alignStartDate(startDate)
  while (currentDate <= endDate) {
    const dateStr = getSimpleISOString(currentDate)

    const dateData = usageMap.get(dateStr) || {
      date: dateStr,
      content: 0,
      media: 0,
    }

    result.push(dateData)
    currentDate = incrementDate(currentDate, 1)
  }

  return result
}

function formatTooltipLabel(data: { id: string | number }): string {
  return capitalize(String(data.id))
}

interface ChartConfig {
  alignStartDate: (date: Date) => Date
  formatXAxisValue: (value: string | number) => string
  incrementDate: (date: Date, amount: number) => Date
  xAxisLegend: string
}

const granularityToChartConfig = {
  daily: {
    alignStartDate: (date) => date,
    formatXAxisValue: (value) => new Date(value).getUTCDate().toString(),
    incrementDate: addDays,
    xAxisLegend: "Days",
  },
  weekly: {
    alignStartDate: startOfWeek,
    formatXAxisValue: (value) => getWeekNumber(new Date(value)).toString(),
    incrementDate: addWeeks,
    xAxisLegend: "Weeks",
  },
  monthly: {
    alignStartDate: startOfMonth,
    formatXAxisValue: (value) => shortMonthFormatter.format(new Date(value)),
    incrementDate: addMonths,
    xAxisLegend: "Months",
  },
} satisfies Record<Granularity, ChartConfig>

const shortMonthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" })
